/**
 * TasksPanel — Active tasks widget for subject dashboard.
 *
 * DRR-321: Extracted from SubjectDashboardPage for widget registry.
 * Shows active tasks grouped by type or priority with shift filtering.
 */

import React, { useState, useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Card,
  Box,
  Skeleton,
  Alert,
  ScrollArea,
} from '@mantine/core';
import { IconChecklist, IconClock } from '@tabler/icons-react';

import {
  useGetTasksBySubjectQuery,
  GqlTaskStatus,
  GqlTaskPriority,
} from '@/generated/graphql';
import { useShiftContext } from '@ferp/workflow-forms';
import type { WidgetProps } from '@ferp/core';

const PRIORITY_COLOR: Record<string, string> = {
  [GqlTaskPriority.CRITICAL]: 'red',
  [GqlTaskPriority.HIGH]: 'orange',
  [GqlTaskPriority.NORMAL]: 'yellow',
  [GqlTaskPriority.LOW]: 'blue',
};

type TaskGroupDimension = 'none' | 'taskGroupRef' | 'priority';

export function TasksPanel({ subjectActorId, tenantId }: WidgetProps) {
  const [groupBy, setGroupBy] = useState<TaskGroupDimension>('taskGroupRef');
  const shift = useShiftContext();

  const { data, loading } = useGetTasksBySubjectQuery({
    variables: { subjectActorId },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const tasks = data?.taskApiGetTasksBySubject ?? [];

  const { activeTasks, completedTasks, criticalCount, highCount, groupedTasks, shiftTaskCount } = useMemo(() => {
    let active = tasks.filter(
      (t) =>
        t.status === GqlTaskStatus.PENDING ||
        t.status === GqlTaskStatus.ASSIGNED ||
        t.status === GqlTaskStatus.IN_PROGRESS,
    );

    const shiftCount = active.filter((t) => shift.isWithinShift(t.dueAt)).length;
    if (shift.shiftFilter === 'current') {
      active = active.filter((t) => shift.isWithinShift(t.dueAt));
    }

    const completed = tasks.filter((t) => t.status === GqlTaskStatus.COMPLETED);
    const critical = active.filter((t) => t.priority === GqlTaskPriority.CRITICAL).length;
    const high = active.filter((t) => t.priority === GqlTaskPriority.HIGH).length;

    const grouped = new Map<string, typeof active>();
    if (groupBy === 'none') {
      grouped.set('All Tasks', active);
    } else {
      for (const task of active) {
        const key =
          groupBy === 'taskGroupRef'
            ? task.taskGroupRef ?? 'Uncategorized'
            : task.priority ?? 'Normal';
        const group = grouped.get(key) ?? [];
        group.push(task);
        grouped.set(key, group);
      }
    }

    const sortedGroups = Array.from(grouped.entries()).sort(([keyA, tasksA], [keyB, tasksB]) => {
      if (groupBy === 'priority') {
        const priorityOrder = { CRITICAL: 0, HIGH: 1, NORMAL: 2, LOW: 3 };
        return (priorityOrder[keyA as keyof typeof priorityOrder] ?? 4) -
               (priorityOrder[keyB as keyof typeof priorityOrder] ?? 4);
      }
      return tasksB.length - tasksA.length;
    });

    return {
      activeTasks: active,
      completedTasks: completed,
      criticalCount: critical,
      highCount: high,
      groupedTasks: sortedGroups,
      shiftTaskCount: shiftCount,
    };
  }, [tasks, groupBy, shift]);

  if (loading && tasks.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (tasks.length === 0) {
    return (
      <Alert icon={<IconChecklist size={16} />} color="blue" variant="light">
        No tasks for this subject.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs">
          <Text fw={600} size="sm">Active Tasks</Text>
          <Badge size="sm" color="orange">{activeTasks.length}</Badge>
          {criticalCount > 0 && (
            <Badge size="xs" color="red">{criticalCount} Critical</Badge>
          )}
          {highCount > 0 && (
            <Badge size="xs" color="orange">{highCount} High</Badge>
          )}
        </Group>
        <Group gap={4}>
          <Text size="xs" c="dimmed">Group:</Text>
          <Badge
            size="xs"
            variant={groupBy === 'taskGroupRef' ? 'filled' : 'outline'}
            style={{ cursor: 'pointer' }}
            onClick={() => setGroupBy('taskGroupRef')}
          >
            Type
          </Badge>
          <Badge
            size="xs"
            variant={groupBy === 'priority' ? 'filled' : 'outline'}
            style={{ cursor: 'pointer' }}
            onClick={() => setGroupBy('priority')}
          >
            Priority
          </Badge>
          <Badge
            size="xs"
            variant={groupBy === 'none' ? 'filled' : 'outline'}
            style={{ cursor: 'pointer' }}
            onClick={() => setGroupBy('none')}
          >
            None
          </Badge>
        </Group>
      </Group>

      {shift.available && (
        <Group gap="xs">
          <IconClock size={14} color="gray" />
          <Text size="xs" c="dimmed">{shift.currentShift?.label}</Text>
          <Badge
            size="xs"
            variant={shift.shiftFilter === 'current' ? 'filled' : 'outline'}
            color="teal"
            style={{ cursor: 'pointer' }}
            onClick={() => shift.setShiftFilter('current')}
          >
            This Shift ({shiftTaskCount})
          </Badge>
          <Badge
            size="xs"
            variant={shift.shiftFilter === 'all' ? 'filled' : 'outline'}
            style={{ cursor: 'pointer' }}
            onClick={() => shift.setShiftFilter('all')}
          >
            All
          </Badge>
        </Group>
      )}

      <ScrollArea.Autosize mah={350}>
        <Stack gap="md">
          {groupedTasks.map(([groupName, groupTasks]) => (
            <Box key={groupName}>
              {groupBy !== 'none' && (
                <Group gap="xs" mb="xs">
                  <Text fw={600} size="xs" c="dimmed">
                    {groupName.replace(/_/g, ' ')}
                  </Text>
                  <Badge size="xs" variant="light">{groupTasks.length}</Badge>
                </Group>
              )}
              <Stack gap="xs">
                {groupTasks.slice(0, groupBy === 'none' ? 8 : 4).map((task) => (
                  <Card key={task.id} withBorder padding="xs" radius="sm">
                    <Group justify="space-between" wrap="nowrap">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={500} size="sm" lineClamp={1}>{task.name}</Text>
                        <Group gap="xs" mt={2}>
                          <Badge
                            size="xs"
                            color={task.status === GqlTaskStatus.IN_PROGRESS ? 'green' : 'yellow'}
                          >
                            {task.status?.replace(/_/g, ' ')}
                          </Badge>
                          {groupBy !== 'priority' && task.priority && (
                            <Badge
                              size="xs"
                              variant="outline"
                              color={PRIORITY_COLOR[task.priority] ?? 'gray'}
                            >
                              {task.priority}
                            </Badge>
                          )}
                        </Group>
                      </Box>
                      {task.mandateRef && (
                        <Badge size="xs" variant="light" color="violet">
                          Regulated
                        </Badge>
                      )}
                    </Group>
                  </Card>
                ))}
                {groupTasks.length > (groupBy === 'none' ? 8 : 4) && (
                  <Text size="xs" c="dimmed" ta="center">
                    +{groupTasks.length - (groupBy === 'none' ? 8 : 4)} more
                  </Text>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      </ScrollArea.Autosize>

      {completedTasks.length > 0 && (
        <Text size="xs" c="dimmed">
          {completedTasks.length} completed today
        </Text>
      )}
    </Stack>
  );
}
